import React, { useState, useCallback, useEffect } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { Pencil, Check, ArrowRightLeft, Loader2, Plus, Trash2 } from "lucide-react";
import { getFlows } from "../../apis/flowAPI";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";
import VariableDropdown from "../FlowBuilder/VariableDropdown";

const TRANSFER_MODES = [
  { value: "all", label: "All variables" },
  { value: "selected", label: "Selected variables" },
  { value: "none", label: "None" },
];

const getTransferSummary = (data) => {
  const mode = data.transferVariableMode || "all";
  if (mode === "none") return "No variables";
  if (mode === "selected") {
    const count = (data.transferVariables || []).filter(Boolean).length;
    return count === 1 ? "1 variable" : `${count} variables`;
  }
  return "All variables";
};

const buildFormState = (data) => ({
  targetFlowId: data.targetFlowId || "",
  targetFlowName: data.targetFlowName || "",
  transferVariableMode: data.transferVariableMode || "all",
  transferVariables: Array.isArray(data.transferVariables) && data.transferVariables.length > 0
    ? data.transferVariables
    : [""],
});

const CollapsedView = ({ data, onEdit }) => {
  const flowName = data.targetFlowName || "";
  const flowId = data.targetFlowId;
  const transferSummary = getTransferSummary(data);

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{ backgroundColor: "#EDE9FE", padding: "12px 12px 8px", minWidth: "240px", maxWidth: "320px" }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); onEdit(); }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit"
      >
        <Pencil size={12} className="text-indigo-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-indigo-100 rounded-lg">
            <ArrowRightLeft size={14} className="text-indigo-600" />
          </div>
          <span className="text-[12px] font-bold text-indigo-900">Transfer to Flow</span>
        </div>

        {flowId ? (
          <>
            <div className="text-[10px] bg-indigo-50 text-indigo-800 px-2 py-1.5 rounded font-medium">
              {flowName || `Flow #${flowId}`}
            </div>
            <p className="text-[9px] text-indigo-700 mt-1.5 font-medium">
              → {transferSummary}
            </p>
          </>
        ) : (
          <p className="text-[10px] text-gray-400 italic">No flow selected</p>
        )}
      </div>
    </div>
  );
};

const TransferVariableRow = ({ index, value, flowVariables, onCreateVariable, onChange, onRemove, canRemove }) => (
  <div className="flex items-center gap-2">
    <span className="text-[10px] text-gray-400 w-5 shrink-0">{index + 1}.</span>
    <VariableDropdown
      value={value}
      onChange={(name) => onChange(name || "")}
      flowVariables={flowVariables || []}
      allowCreate
      onCreateVariable={onCreateVariable}
      placeholder="Select variable..."
      className="flex-1"
    />
    {canRemove && (
      <button
        type="button"
        onClick={onRemove}
        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg shrink-0"
        title="Remove variable"
      >
        <Trash2 size={14} />
      </button>
    )}
  </div>
);

const ExpandedEditor = ({ data, onSave, onCancel, flowVariables, onCreateVariable }) => {
  const [form, setForm] = useState(() => buildFormState(data));
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getFlows({})
      .then((res) => {
        if (!cancelled) setFlows(res.data.flows || []);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleSelect = (e) => {
    const id = e.target.value;
    const found = flows.find((f) => f.id === Number(id));
    setForm((prev) => ({
      ...prev,
      targetFlowId: id ? Number(id) : "",
      targetFlowName: found?.name || "",
    }));
  };

  const updateMode = (mode) => {
    setForm((prev) => ({
      ...prev,
      transferVariableMode: mode,
      transferVariables: mode === "selected" && prev.transferVariables.length === 0 ? [""] : prev.transferVariables,
    }));
  };

  const updateVariable = (idx, name) => {
    setForm((prev) => ({
      ...prev,
      transferVariables: prev.transferVariables.map((v, i) => (i === idx ? name : v)),
    }));
  };

  const addVariable = () => {
    setForm((prev) => ({ ...prev, transferVariables: [...prev.transferVariables, ""] }));
  };

  const removeVariable = (idx) => {
    setForm((prev) => ({
      ...prev,
      transferVariables: prev.transferVariables.filter((_, i) => i !== idx),
    }));
  };

  const handleSave = () => {
    const transferVariables = form.transferVariableMode === "selected"
      ? [...new Set(form.transferVariables.map((v) => String(v).trim()).filter(Boolean))]
      : [];

    onSave({
      targetFlowId: form.targetFlowId || null,
      targetFlowName: form.targetFlowName,
      transferVariableMode: form.transferVariableMode || "all",
      transferVariables,
    });
  };

  return (
    <div
      className="nowheel"
      style={{ backgroundColor: "#EDE9FE", borderRadius: "12px", padding: "14px", minWidth: "340px", maxWidth: "400px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ArrowRightLeft size={16} className="text-indigo-600" />
          <span className="text-[13px] font-bold text-indigo-900">Transfer to Flow</span>
        </div>
        <div className="flex gap-1.5">
          <button onClick={handleSave} className="px-3 py-1.5 text-[11px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1 shadow-sm">
            <Check size={12} />Save
          </button>
          <button onClick={onCancel} className="px-3 py-1.5 text-[11px] font-bold bg-white text-[#667781] rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="transferToFlow">
        <div className="bg-white rounded-lg shadow-sm p-3 space-y-3">
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Destination Flow</label>
            {loading ? (
              <div className="flex items-center gap-2 py-2">
                <Loader2 size={14} className="text-indigo-500 animate-spin" />
                <span className="text-[10px] text-gray-500">Loading flows...</span>
              </div>
            ) : (
              <select
                value={form.targetFlowId || ""}
                onChange={handleSelect}
                className="w-full px-2 py-1.5 text-[11px] bg-[#F0F0F0] rounded-lg border-0 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">Select a flow...</option>
                {flows.map((f) => (
                  <option key={f.id} value={f.id}>{f.name} (#{f.id})</option>
                ))}
              </select>
            )}
            <p className="text-[8px] text-gray-400">
              The current flow will end and the user will be transferred to the selected flow.
            </p>
          </div>

          <div className="space-y-2 pt-1 border-t border-gray-100">
            <label className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Variables to Transfer</label>
            <div className="space-y-1.5">
              {TRANSFER_MODES.map((mode) => (
                <label key={mode.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="transferVariableMode"
                    value={mode.value}
                    checked={(form.transferVariableMode || "all") === mode.value}
                    onChange={() => updateMode(mode.value)}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-[11px] text-gray-700">{mode.label}</span>
                </label>
              ))}
            </div>

            {form.transferVariableMode === "selected" && (
              <div className="space-y-2 pt-1">
                <div className="flex items-center justify-between">
                  <span className="text-[9px] font-bold text-gray-500 uppercase">Pick variables</span>
                  <button
                    type="button"
                    onClick={addVariable}
                    className="text-[9px] font-bold text-indigo-600 hover:text-indigo-700 flex items-center gap-0.5"
                  >
                    <Plus size={9} /> Add
                  </button>
                </div>
                {form.transferVariables.map((name, idx) => (
                  <TransferVariableRow
                    key={idx}
                    index={idx}
                    value={name}
                    flowVariables={flowVariables}
                    onCreateVariable={onCreateVariable}
                    onChange={(v) => updateVariable(idx, v)}
                    onRemove={() => removeVariable(idx)}
                    canRemove={form.transferVariables.length > 1}
                  />
                ))}
                <p className="text-[8px] text-gray-400">
                  Only variables that exist at transfer time are copied. Session defaults (phone, name, etc.) are always set in the new flow.
                </p>
              </div>
            )}
          </div>
        </div>
      </NodeEditorTabs>

      <div className="flex gap-2 mt-3">
        <button onClick={handleSave} className="flex-1 py-2 text-[12px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm">
          <Check size={14} />Save
        </button>
        <button onClick={onCancel} className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200">
          Cancel
        </button>
      </div>
    </div>
  );
};

const TransferToFlowNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("transferToFlow");

  const handleSave = useCallback((formData) => {
    if (data.onNodeDataChange) data.onNodeDataChange(id, { ...data, ...formData });
    closeEditor();
  }, [id, data, closeEditor]);

  return (
    <div className={`relative transition-all shadow-md`} style={{ borderRadius: "12px" }}>
      <NodeActionButtons id={id} data={data} />
      <Handle type="target" position={Position.Left} className="!w-3.5 !h-3.5 !bg-indigo-500 !border-2 !border-white !shadow-sm" style={{ left: -7 }} />
      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={closeEditor}
          flowVariables={data.flowVariables || []}
          onCreateVariable={data.onCreateVariable}
          presentation="fullscreen"
        />
      </FlowNodeEditorShell>
      <Handle type="source" position={Position.Right} id="default" className="!w-3.5 !h-3.5 !bg-indigo-500 !border-2 !border-white !shadow-sm" style={{ right: -7 }} />
    </div>
  );
};

export default TransferToFlowNode;
